import { ApiError } from "./api";
import {
  submitMarketplaceReview,
  upgradeMarketplaceReview,
  type MyReview,
  type MyReviewState,
  type ReviewIncentiveHonored,
} from "./marketplace-connection-api";

/**
 * Review stage rules (operator 2026-10-09): one review per pair, at most two
 * asks, each answered once, never edited. The SERVER decides the ask
 * (`next_ask` on `GET /api/marketplace/reviews`); this module is the ONE
 * place a buyer surface turns that decision into what to render and which
 * write to make. Home's review prompt, the check-in modal, the `/offers`
 * deep link, the Your Team card, and the relationship page all go through
 * here, so no surface can re-offer an answered ask.
 */

export type ReviewAskView =
  /** No review yet: offer the worked_together capture sheet. */
  | { kind: "capture" }
  /** The closed ask: the close-upgrade modal, pre-filled from the existing
   *  worked_together review when there is one. */
  | {
      kind: "close";
      prefill: Pick<MyReview, "stars" | "body"> | null;
      confirmRequired: boolean;
    }
  /** Answered: show the review read-only, no edit control. */
  | { kind: "readonly"; review: MyReview }
  /** Nothing to show (still loading, ineligible, or removed). */
  | { kind: "none" };

export function reviewAskView(
  state: MyReviewState | null | undefined,
): ReviewAskView {
  if (!state) return { kind: "none" };
  if (state.nextAsk === "worked_together") return { kind: "capture" };
  if (state.nextAsk === "closed") {
    return {
      kind: "close",
      prefill: state.review
        ? { stars: state.review.stars, body: state.review.body }
        : null,
      confirmRequired: state.closeConfirmRequired,
    };
  }
  if (state.review) return { kind: "readonly", review: state.review };
  return { kind: "none" };
}

/** True when a surface whose only job is to ASK should render at all. */
export function reviewAskIsOpen(view: ReviewAskView): boolean {
  return view.kind === "capture" || view.kind === "close";
}

export interface ReviewAnswer {
  stars: number;
  body: string;
  /** Close stage only — the optional "was the incentive honored" chip. */
  incentiveHonored?: ReviewIncentiveHonored;
  /** Close stage only — the buyer's in-sheet "we closed" confirmation. */
  confirmClosed?: boolean;
}

/**
 * Writes the buyer's answer to the ask the server offered. The write follows
 * `state.nextAsk`, never the surface: worked_together creates the review; the
 * closed ask upgrades the existing review in place, or creates one and then
 * upgrades it when there is none yet.
 */
export async function submitReviewAnswer(
  getToken: () => Promise<string | null>,
  proUserId: string,
  state: MyReviewState,
  answer: ReviewAnswer,
): Promise<void> {
  if (state.nextAsk === "worked_together") {
    await submitMarketplaceReview(getToken, proUserId, answer.stars, answer.body);
    return;
  }
  if (state.nextAsk === "closed") {
    const id =
      state.review?.id ??
      (await submitMarketplaceReview(getToken, proUserId, answer.stars, answer.body)).id;
    await upgradeMarketplaceReview(
      getToken,
      id,
      answer.stars,
      answer.body,
      answer.incentiveHonored,
      answer.confirmClosed,
    );
    return;
  }
  throw new Error("review-ask: nothing to answer");
}

/** Maps the close modal's "partially" chip onto the wire's "partial"; "yes"
 *  and "no" pass through, and an unanswered chip stays undefined. */
export function toWireIncentive(
  value: "yes" | "partially" | "no" | undefined,
): ReviewIncentiveHonored | undefined {
  if (value === "partially") return "partial";
  return value;
}

export type ReviewWriteOutcome =
  | { kind: "posted"; message: string }
  | { kind: "refused"; message: string }
  | { kind: "failed"; message: string };

/** The confirmation every successful review submit shows. */
export function reviewPostedMessage(proName: string): string {
  return `Thanks — your review of ${proName} is posted.`;
}

/**
 * Tells the author why a just-posted (or read-only) invited review is not yet
 * visible on the agent's profile. Server-owned: `counts_after` is the only
 * input; origin is never guessed client-side. Null when absent, unparseable,
 * or not in the future. Month/day are formatted in UTC so the sentence is
 * stable across time zones.
 */
export function reviewVisibilityNote(
  review: Pick<MyReview, "counts_after"> | null | undefined,
  now: Date,
  proName?: string,
): string | null {
  const raw = review?.counts_after;
  if (!raw) return null;
  const at = new Date(raw);
  if (Number.isNaN(at.getTime()) || at.getTime() <= now.getTime()) return null;
  const date = at.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
  const owner = proName ? `${proName}'s` : "their";
  return `Your review will appear on ${owner} profile on ${date}.`;
}

/** Shown in place of the form when the server refuses a repeat (409). */
export function alreadyReviewedMessage(proName: string): string {
  return `You've already reviewed ${proName}.`;
}

/** Maps a submit failure onto what the buyer sees. A 409 is never silently
 *  dropped: the review exists, so say so. */
export function reviewWriteFailure(error: unknown, proName: string): ReviewWriteOutcome {
  if (error instanceof ApiError && error.status === 409) {
    if (error.code === "close_not_confirmed") {
      return {
        kind: "refused",
        message: `Confirm that you closed with ${proName} to leave a closing review.`,
      };
    }
    return { kind: "refused", message: alreadyReviewedMessage(proName) };
  }
  return { kind: "failed", message: "Couldn't post your review — try again." };
}
